<template>
  <div>
    <component :is="asModal ? 'modal': 'block'"
               size="xl"
               :title="modalTitle"
               :center-title="true"
               :show-footer="true"
               :show="isOpen"
               @modal:closed="closeModal"
               v-bind="modalProps">
      <template #default>
        <div class="container margin-auto item-orderer">
          <p class="text-center margin-l-bottom text-l">
            {{ tu("ls.sortUi.explain") }}
          </p>
          <spinner :size="4" v-if="!showSortableUi"></spinner>
          <div
            :style="isLoading ? 'cursor: loading;opacity: 0.5;': ''"
            class="padding bg-gray border-radius flex-column gap-l"
            v-if="showSortableUi"

            v-sortable="{ onEnd: onReorder, handle: '[data-role=handle]' }">

            <div

              class="item animate__fadeIn flex-middle gap-m bg-bg border-radius padding-s"
              v-for="(item, index) in localItems"
              :key="`${idKey}-${item[idKey]}`"
              :style="`cursor: ${itemExclusionFilter(item) || isLoading ? 'not-allowed' : 'grab'};`"
              :class="{ 'bg-text': itemExclusionFilter(item), 'c-bg': itemExclusionFilter(item) }">
              <icon
                v-if="!itemExclusionFilter(item)"
                icon="align-justify-alt"
                :size="2"
                data-role="handle"></icon>
              <icon
                v-else
                icon="check"
                :size="2"
                theme="success"
                style="cursor: not-allowed"></icon>
              <slot name="item" v-bind="{item}">
                <div class="flex-middle gap-m">
            <span class="text-overflow-ellipsis" style="flex: auto">{{
                item.title
              }}</span>
                </div>
              </slot>
            </div>
          </div>
        </div>
      </template>

      <template #footer>
        <div class="flex-middle flex-center margin-auto-inline gap-l">
          <form-button v-if="immediateReorder"
                       icon="x"
                       text="ls.sortUi.close"
                       :loading="isLoading"
                       :disabled="isLoading"
                       @click="closeModal"></form-button>

          <form-button v-if="! immediateReorder"
                       icon="x"
                       theme="inverse"
                       text="ls.sortUi.cancel"
                       :loading="isLoading"
                       :disabled="isLoading"
                       @click="cancelReorder"></form-button>

          <form-button v-if="! immediateReorder"
                       icon="check"
                       theme="success"
                       text="ls.sortUi.confirm"
                       :loading="isLoading"
                       :disabled="isLoading"
                       @click="confirmReorder"></form-button>
        </div>
      </template>
    </component>

  </div>

</template>

<script>
import asyncOperations from "@/client/extensions/composition/asyncOperations";
import { isRef, unref, isReactive, isProxy, toRaw } from "vue";

export default {
  setup(props) {
    let { asyncOps, asyncOpsReady } = asyncOperations(props);
    return {
      asyncOps,
      asyncOpsReady,
    };
  },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    endpoint: {
      type: String,
      default: "",
    },
    itemExclusionFilter: {
      type: Function,
      default: () => {
        return false;
      },
    },
    immediateReorder: {
      type: Boolean,
      default: true,
    },
    modelValue: {
      type: [Array, Boolean],
      default: false,
    },
    modalTitle: {
      type: [String, Boolean],
      default: false,
    },
    modalProps: {
      type: Object,
      default: () => {
      },
    },
    asModal: {
      type: Boolean,
      default: false,
    },
    idKey: {
      type: String,
      default: "id",
    },
    /**
     * v-model for being opened / closed when presented as a modal
     */
    isOpen: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showSortableUi: true,
      localItems: [],
      localItemsCopy: [],
      isLoading: false,
    };
  },
  watch: {
    items: {
      handler(newVal) {
        this.localItems = this.getRawItems(newVal);
      },
      deep: true,
      immediate: true,
    },
  },
  computed: {
    value: {
      get() {
        if (!this.modelValue) {
          return this.localItems;
        }

        return this.modelValue;
      },
      set(val) {
        if (Array(this.modelValue)) {
          this.$emit("update:modelValue", val);
        }

        this.localItems = val;
      },
    },
  },
  emits: ["itemOrderer:success", "update:isOpen"],
  methods: {
    /**
     * Recursively removes Vue reactivity wrappers (proxy / ref)
     * so the result can be freely JSON-stringified or structured-cloned.
     */
    deepToRaw(value) {
      // unwrap ref first (returns .value, may still be proxy)
      if (isRef(value)) {
        value = unref(value);
      }

      // unwrap one proxy layer
      if (isProxy(value)) {
        value = toRaw(value);
      }

      // recurse through containers
      if (Array.isArray(value)) {
        return value.map(this.deepToRaw);
      }

      if (value && typeof value === "object") {
        const out = {};
        for (const [k, v] of Object.entries(value)) out[k] = this.deepToRaw(v);
        return out;
      }

      // primitives (string, number, boolean, null, undefined)
      return value;
    },
    getRawItems(items) {
      return structuredClone(this.deepToRaw(items));

    },
    async confirmReorder() {
      this.isLoading = true;
      let updateData = this.localItems.map(item => item.id);
      let reqData = { newOrdering: updateData };

      let result = await this.asyncOps.asyncCall(
        this.endpoint,
        reqData,
        {
          method: "patch",
        });

      if (!result || result.hasError) {
        this.$s.ui.notification("core.genericError", "error");
      }

      this.isLoading = false;
      this.$emit("itemOrderer:success", { items: this.localItems, itemOrderer: this });
      this.value = this.getRawItems(this.localItems);
      // todo: update v-model value
    },
    async restoreOriginalOrder() {
      this.localItems = this.getRawItems(this.items);
    },
    async cancelReorder() {
      this.restoreOriginalOrder();
      this.closeModal();
    },

    async onReorder(e, b) {
      const list = this.localItems;
      const [moved] = list.splice(e.oldIndex, 1);
      list.splice(e.newIndex, 0, moved);

      if (this.immediateReorder) {
        await this.$nextTick();
        this.confirmReorder();
      }
    },
    closeModal() {
      this.$emit("update:isOpen", false);
    },
  },
};
</script>
